import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import theme from 'styled-theming';

import BinaryQuestion from '~/components/binary-question';
import { Modal } from '~/components/modal/modal-old';
import useTranslations from '~/components/translation/use-translations';
import Loading from '~/components/misc/loading';
import currencyFormatter from '~/libs/currency-formatter';
import { updateBuyIn } from '../../ducks/we-spin-duck';
import { WE_SPIN_BUY_IN_TEXT_KEYS, WE_SPIN_MODALS_TEXT_KEYS } from '../../constants/we-spin-text-keys';
import { InputHint } from '../../game-room/we-spin-game-room-buy-in-base';
import { selectBalance, selectCurrency } from '~/components/account/ducks';
import { selectLocale } from '~/components/brand/ducks/selectors';
import { selectParticipations } from '../../ducks/we-spin-selectors';
import { authenticationSelector } from '~/components/authentication/ducks/selectors';
import useLoginFlow from '~/hooks/use-login-flow';
import { selectCurrentEvent } from '../../ducks/we-spin-home-game-duck';

const BinaryQuestionWrapper = styled(BinaryQuestion)`
	p {
		color: rgba(var(--font-supportive), 1);

		${theme('brand', {
			comeon: css`
				color: rgba(var(--font-light), 1) !important;
			`,
			suomikasino: css`
				color: rgba(var(--font-dark), 1) !important;
			`,
			getlucky: css`
				color: rgba(var(--font-dark), 1) !important;
			`,
			mobilebet: css`
				color: rgba(var(--font-light), 1) !important;
			`,
			nopeampi: css`
				color: rgba(var(--font-dark), 1) !important;
			`,
			galaksino: css`
				color: rgba(var(--font-light), 1) !important;
			`
		})}
	}

	input {
		text-align: center;
	}
`;

const TEXT_KEYS = [...WE_SPIN_BUY_IN_TEXT_KEYS, ...WE_SPIN_MODALS_TEXT_KEYS];

/**
 * Modal component for adjusting the buy-in amount in a stream session.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.openAdjustBuyin - Whether the modal is open.
 * @param {Function} props.closeAdjustBuyin - Function to close the modal.
 * @param {string} props.streamRef - Reference ID for the stream session.
 * @returns {JSX.Element}
 */
const AdjustBuyinModal = props => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { translations, isLoadingTranslations } = useTranslations(TEXT_KEYS);

	const streamView = useSelector(selectCurrentEvent) || {};
	const locale = useSelector(selectLocale);
	const playerCurrency = useSelector(selectCurrency);
	const [submitting, setSubmitting] = useState(false);
	const authenticated = useSelector(authenticationSelector);
	const { initializeLogin } = useLoginFlow();
	const participations = useSelector(selectParticipations);
	const myByIn = useMemo(
		() => participations?.find(stream => stream.streamRef === props.streamRef)?.buyIn || 0,
		[participations, props.streamRef]
	);
	const [newBalance, setNewBalance] = useState();
	const [lowestBuyInAmount] = useState(0);
	const walletBalanceInPlayersCurrency = useSelector(selectBalance) || 0;
	const onChangeEditAmountHandler = useCallback(e => {
		setNewBalance(e.target.value.replace(/^(\d+\.\d{2}).*$/, '$1'));
	}, []);

	const onAdjustAmount = useCallback(async () => {
		if (!authenticated) {
			initializeLogin({
				from: `${location.pathname}${location.search}`
			});
			return;
		}

		try {
			setSubmitting(true);
			await dispatch(updateBuyIn(props.streamRef, parseInt(newBalance, 10), () => {}));
			props.closeAdjustBuyin();
		} finally {
			setSubmitting(false);
		}
	}, [authenticated, dispatch, initializeLogin, location.pathname, location.search, newBalance, props]);

	if (isLoadingTranslations) return <Loading />;

	return (
		<Modal show={props.openAdjustBuyin} position="fixed">
			<BinaryQuestionWrapper
				shouldOpenBinary
				openInSidebar
				hasInput
				inputValue={newBalance}
				inputType="number"
				inputPattern="\d+"
				min={lowestBuyInAmount}
				maxlength={20}
				placeholder={translations.WE_SPIN_PREVIEW_ROOM_EDIT_AMOUNT_PLACEHOLDER}
				inputLabel=""
				onChange={onChangeEditAmountHandler}
				enableInputValidation
				disableYesClick={
					newBalance < lowestBuyInAmount ||
					newBalance > streamView.balanceCap - streamView.balance + myByIn ||
					newBalance > walletBalanceInPlayersCurrency
				}
				buttonsHasUncontrolledLoading={submitting}
				title={translations.WE_SPIN_BUY_IN_ADJUST_AMOUNT_TITLE}
				text={`${translations.WE_SPIN_BUY_IN_ADJUST_AMOUNT_CURRENT_BUYIN_TEXT} ${currencyFormatter.format(
					myByIn,
					playerCurrency,
					locale
				)}`}
				onYesClick={onAdjustAmount}
				onNoClick={props.closeAdjustBuyin}
				yesText={translations.WE_SPIN_BUY_IN_ADJUST_AMOUNT_CONFIRM}
				noText={translations.WE_SPIN_BUY_IN_ADJUST_AMOUNT_CANCEL}
			>
				{newBalance !== '' && newBalance < lowestBuyInAmount && (
					<InputHint className="hint" error={newBalance < lowestBuyInAmount}>
						{`${currencyFormatter.format(lowestBuyInAmount, playerCurrency, locale)} ${
							translations.WE_SPIN_BUY_IN_MINIMUM_AMOUNT
						}`}
					</InputHint>
				)}
				{newBalance > streamView.balanceCap - (streamView.balance - myByIn) &&
					(streamView.balance * 100) / streamView.balanceCap < 90 && (
						<InputHint className="hint" error={newBalance > streamView.balanceCap - streamView.balance}>
							{`${translations.WE_SPIN_BUY_IN_MAX_AMOUNT_REACHED} ${currencyFormatter.format(
								streamView.balanceCap - (streamView.balance - myByIn),
								playerCurrency,
								locale
							)}`}
						</InputHint>
					)}
				{(streamView.balance * 100) / streamView.balanceCap - (streamView.balance - myByIn) >= 90 && (
					<InputHint className="hint" error={newBalance > streamView.balanceCap}>
						{`${translations.WE_SPIN_BUY_IN_MAX_AMOUNT_ALMOST_REACHED} ${currencyFormatter.format(
							streamView.balanceCap - (streamView.balance - myByIn),
							playerCurrency,
							locale
						)}`}
					</InputHint>
				)}
				{newBalance > walletBalanceInPlayersCurrency && (
					<InputHint className="hint" error>
						{`${translations.WE_SPIN_BUY_IN_INSUFFICIENT_FUNDS}`}
					</InputHint>
				)}
			</BinaryQuestionWrapper>
		</Modal>
	);
	// const location = useLocation();
	// const playerCurrency = useSelector(selectCurrency);
	// const locale = useSelector(selectLocale);
	// const { translations: tr, isLoadingTranslations } = useTranslations(WE_SPIN_MODALS_TEXT_KEYS);
	//
	// const buyInBalance = useMemo(
	// 	() => (buyInAmount > 0 ? currencyFormatter.format(buyInAmount || 0, playerCurrency, locale) : ''),
	// 	[buyInAmount, playerCurrency, locale]
	// );

	// const participations = useSelector(selectParticipations);
	// const myByIn = useMemo(() => {
	// 	return participations?.find(stream => stream.streamRef === props.streamRef)?.buyIn || 0;
	// }, [participations, props.streamRef]);
	// const [newBalance, setNewBalance] = useState(myByIn);
	// const [lowestBuyInAmount, setLowestBuyInAmount] = useState(0);
	// const walletBalance = useSelector(selectPlayerBalanceInEventCurrency(props.streamRef)) || 0;
	// const hasBuyInAmountChecks = useMemo(() => !!props.isPrivateStreamer, [props.isPrivateStreamer]);
	// useEffect(() => {
	// 	setLowestBuyInAmount(props.buyinAmounts[0]);
	// }, [walletBalance, setLowestBuyInAmount, props.buyinAmounts, hasBuyInAmountChecks]);
	// const walletBalanceInPlayersCurrency = useSelector(selectBalance) || 0;
};

export default AdjustBuyinModal;
